import type { ResidueEntry } from "./order33State.ts";
/**
 * Read-API client. The frontend half of the fail-loud contract (law 1).
 *
 * THE RULE THIS FILE ENFORCES: a failure produces a FeedError that the UI must render.
 * There is no code path here that turns an error into an empty array, a stale cache hit,
 * or a resolved promise with nothing in it. Panels get either real data with its age, or
 * an error they are obliged to show. "The chart is empty" must never be the way a user
 * discovers the backend is down.
 */

import { expiryTidePath, type ExpirationScope, type ExpiryTide } from "./expiryTide";

export interface Candle {
  t: number; // epoch ms, bar open
  o: number;
  h: number;
  l: number;
  c: number;
  v: number | null;
}

/** Staleness metadata carried by every data response. Computed server-side (law 10). */
export interface FeedMeta {
  root: string;
  symbol: string;
  tf: string;
  vendor_ts: string;
  receipt_ts: string;
  age_s: number;
  budget_s: number;
  stale: boolean;
  poll_seq: number;
  served_at: string;
}

export interface CandlesResponse extends FeedMeta {
  declared_residue?: readonly ResidueEntry[];
  bars: Candle[];
  count: number;
  limit_applied: number;
  limit_capped: boolean;
}

/** A failure the UI is REQUIRED to render. Never swallowed, never defaulted away. */
export class FeedError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail: string | null = null,
    readonly refusedRows?: unknown,
  ) {
    super(message);
    this.name = "FeedError";
  }
}

/**
 * The read-API origin. Injected at build time so the deployed page talks to the
 * deployed Worker; falls back to the known workers.dev host for local `vite dev`.
 */
import type { TicksPayload } from "./tickApi";
// The lane's ask lives with the lane (and is armed there): `api.ts` carries a TS parameter
// property, so Node's strip-only test runner cannot import THIS module at all. A predicate
// parked here would be unarmable — which is the state the whole R55a defect grew in.
import { cvdPath } from "./cvdLane";
// The failure-message decision lives in its own module BECAUSE this one cannot be arm-imported.
import { feedRefusedRows, feedErrorMessage, isFeedFailure, type FeedErrorBody } from "./feedError";
import { dealerScopePath, type DealerScope } from "./dealerScope";
import { createVwapStore, type VwapLevel, type VwapLevelsResponse, type VwapStoreSnapshot } from "./vwapStore";
import { noteTideScaleVenueState } from "./tideScale";
import { ageServedValue, sharedCoalescer } from "./requestCoalescer";
import { pollIntervalMs } from "./tickPoll";
export type { VwapLevel, VwapLevelsResponse, VwapStoreSnapshot } from "./vwapStore";

/** Exported for R765's primary chart, which fetches its own two legs and must not re-derive
    (or drift from) the origin every other call in this app already uses. */
export const API_BASE: string =
  import.meta.env.VITE_SPX_API_BASE ??
  "https://spx-read-api.fd1cf3f4-3cbb-4c12-8f01-6559d93a9be9.workers.dev";

/** Client-side ceiling. The Worker enforces its own; this stops silly requests early. */
const REQUEST_TIMEOUT_MS = 12_000;

async function getJson<T>(path: string, origin = API_BASE): Promise<T> {
  const url = new URL(path, origin).href;
  const params = new URL(url).searchParams;
  const history = params.has("before") || params.has("from") || params.has("to");
  const ttlMs = history ? 0 : /\/api\/es\/(ticks|candles|cvd)$/.test(new URL(url).pathname)
    ? pollIntervalMs() : /\/api\/es\/wall-alerts$/.test(new URL(url).pathname)
      ? 5_000 : /\/api\/es\/vwap-levels$/.test(new URL(url).pathname)
        ? 60_000 : /\/api\/dealer\/levels$/.test(new URL(url).pathname)
          ? 180_000 : 0;
  // The cache stores one envelope shape for both doors. A chart leg can read an API
  // client's answer (and vice versa) without mistaking the whole envelope for a body.
  let result: { body: unknown; transport: string | null };
  try {
    const hit = await sharedCoalescer.get(url, async () => ({
      body: await requestJson<T>(url), transport: null,
    }), ttlMs);
    result = { ...hit.value, body: ageServedValue(hit.value.body, hit.cacheAgeMs) };
  } catch (error) {
    if (error && typeof error === "object" && "transport" in error) {
      throw new FeedError(String(error.transport), 0);
    }
    throw error;
  }
  if (result.transport !== null) throw new FeedError(result.transport, 0);
  return result.body as T;
}

async function requestJson<T>(url: string): Promise<T> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), REQUEST_TIMEOUT_MS);

  try {
    let res: Response;
    try {
      res = await fetch(url, {
        signal: ctl.signal,
        headers: { Accept: "application/json" },
      });
    } catch (err) {
      // Network failure / timeout. Surfaced, not swallowed.
      throw new FeedError(
        ctl.signal.aborted ? "request timed out" : "network error reaching the read-API",
        0,
        err instanceof Error ? err.message : String(err),
      );
    }

    let body: unknown;
    try {
      body = await res.json();
    } catch (err) {
      if (ctl.signal.aborted) {
        throw new FeedError("request timed out", 0, err instanceof Error ? err.message : String(err));
      }
      throw new FeedError("read-API returned a non-JSON body", res.status);
    }

    const b = body as FeedErrorBody;

  // Two independent checks. A 200 carrying `ok: false` is still a failure, and trusting
  // the status code alone is how a fail-soft response sneaks through as data.
  //
  // ⛔⛔ `reason` IS READ BECAUSE THE WORKER HAS **TWO** ERROR CONVENTIONS AND THIS LINE KNEW ONE.
  // Measured live 2026-08-22 08:0xZ, on the owner's deployed page:
  // ```
  //   index.ts:268  fail()   -> { ok:false, error, detail }   the NON-200 path
  //   index.ts:1777 ladder   -> { ok:false, root, reason }    200-with-ok:false
  //   index.ts:1875 levels   -> { ok:false, root, reason }    (also :714 :866 :1017)
  //   live keys on BOTH dealer routes: ['ok','reason','root'] · error absent
  // ```
  // With `error` alone, every 200-with-`ok:false` lost its sentence and fell through to the
  // status fallback — so §GEX-2 rendered **"0DTE ladder unavailable — read-API returned HTTP 200"**
  // to the owner: a contradiction that reads as a broken pane sitting on a perfectly healthy stack.
  // ⇒ ***THE ROUTE'S HONEST REASON EXISTED AT EVERY LAYER AND DIED ONE LINE BEFORE THE READER.***
  // ⚠ AND THE FALLBACK IS WHAT HID IT: a message that always produces *something* plausible turns a
  //   dropped field into a cosmetic oddity instead of a fault. `?? ` is not free — **a default that
  //   can never look wrong is a default that can never be caught.**
  // ⚠ ORDER IS DELIBERATE: `error` first, so the keyed `fail()` contract keeps precedence and this
  //   is purely additive for every route that already worked.
  // 📌 THE DECISION LIVES IN `feedError.ts`, NOT HERE, and that is the actual cure: this module is
  //   un-importable by the strip-only test runner, so anything decided inline is UNARMABLE — and
  //   the unarmable line is the one that was wrong. See that file's header.
    if (isFeedFailure(res.ok, b)) {
      throw new FeedError(feedErrorMessage(b, res.status), res.status, b?.detail ?? null,
        Object.hasOwn(b ?? {}, "refused_rows") ? feedRefusedRows(b) : undefined);
    }

    return body as T;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Tick-tape state. The payload is bound to a `TickProbe` in `tickApi.ts`, never read raw by a panel:
 * the rungs are semantic states and this is a wire format, and DESK-n8 ruled they stay separate
 * objects so a re-shape has one blast radius.
 */
export function fetchTicks(symbol = "ES1!", limit = 10): Promise<TicksPayload> {
  return getJson<TicksPayload>(
    `/api/es/ticks?symbol=${encodeURIComponent(symbol)}&tf=1m&limit=${limit}`,
  );
}

export function fetchCandles(symbol = "ES1!", limit = 300, tf?: string): Promise<CandlesResponse> {
  const rung = tf === undefined ? "" : `&tf=${encodeURIComponent(tf)}`;
  return getJson<CandlesResponse>(
    `/api/es/candles?symbol=${encodeURIComponent(symbol)}&limit=${limit}${rung}`,
  );
}

/**
 * RANGED candles — the chart P1 scroll-back page (`/api/es/candles?from=&to=`, half-open
 * [from,to) epoch ms). ⚠ ITS OWN TYPE, not CandlesResponse, for two honest reasons:
 *
 *  · `window` is the CAPABILITY ECHO. The deployed Worker predates the ranged query until
 *    its gate-keyed deploy lands, and an older deploy IGNORES params it never knew — same
 *    bars back, no error. The echo is how the client tells "server honoured my range" from
 *    "server never heard it": `window` absent ⇒ fall back, never assume.
 *  · a ranged window can be EMPTY (weekend, halt, pre-table history) — a 200 FACT with
 *    `range_empty: true` and NO staleness fields, because there is no newest row to derive
 *    them from. CandlesResponse's non-null staleness contract would claim fields the
 *    payload does not have.
 *
 * ⛔ HISTORY PAGES MUST NEVER FEED A STALENESS CHIP. Their `stale`/`age_s` (when present)
 * are truthful about an OLD window and would paint a healthy panel amber. Chips read the
 * live poll only.
 */
export interface RangedCandlesResponse {
  declared_residue?: readonly ResidueEntry[];
  ok: true;
  symbol: string;
  root: string;
  tf: string;
  bars: Candle[];
  count: number;
  /** Echo of the honoured window. ABSENT ⇒ the deploy ignored from/to — do not page. */
  window?: { from: number; to: number };
  range_empty?: boolean;
  limit_applied: number;
  limit_capped: boolean;
  served_at: string;
}

export function fetchCandlesRange(
  symbol: string,
  fromMs: number,
  toMs: number,
  limit = 1440,
): Promise<RangedCandlesResponse> {
  return getJson<RangedCandlesResponse>(
    `/api/es/candles?symbol=${encodeURIComponent(symbol)}&limit=${limit}&from=${fromMs}&to=${toMs}`,
  );
}

/**
 * `/api/es/cvd` — the SPX-060 READ LEG (BUILDER-kk, landed 6485189d / deployed 63e255a1).
 * One 1m signed-CVD bar per row, oldest-first, `t` = bar_open epoch ms on the SAME minute
 * grid as `/api/es/candles` — which is what makes exact-`t` alignment onto the candle
 * axis valid (see cvdLane.ts).
 *
 * Mirrors `workers/read-api/src/cvd.ts`'s served shape; field-for-field, not extended from
 * FeedMeta, because this envelope genuinely differs: no `vendor_ts`/`symbol`/`tf`, and
 * `age_s`/`budget_s`/`stale` are NULL on the EMPTY register rather than always-present.
 */
export interface CvdBar {
  t: number; // epoch ms, bar open — same grid as Candle.t
  instrument: string | null; // travels PER BAR; NULL on a refused (ambiguous) bucket
  cvd: number | null; // CONTINUOUS cumulative signed volume; NULL when the bucket is refused
  /** Native cumulative-delta candle; all-null means a declared legacy/pre-033 wickless row. */
  cvd_o?: number | null;
  cvd_h?: number | null;
  cvd_l?: number | null;
  delta: number | null; // THIS bar's signed volume, the server's own; NULL when refused
  n_signed: number;
  n_unsigned: number;
  runner_up: string | null;
  roll_pressure: number;
  excluded_spreads: number;
  /**
   * SIGNED PRESSURE, `delta / n_signed` — the owner's Pine `press` with a real tape under it
   * (spec `3a28c861`, af's divisor cure `@59127d10`, SERVED since the 2026-08-18 worker deploy).
   *
   * ⛔ NULL WHEN `n_signed = 0`, AND THAT NULL IS LOAD-BEARING: DESK-n21 R28 ruled the condition
   * fleet-wide as NO ANSWER, never a real zero. Optional because the field post-dates earlier
   * deployments — and `undefined` must be read as "this worker predates the field", NEVER as 0.
   */
  press_v1?: number | null;
  signed_src_bars?: number;
  /**
   * The route's OWN refusal flag, set when a bucket resolves to more than one instrument
   * label. When true, `cvd`/`delta`/`instrument` arrive NULL — the bucket exists and carries
   * no value.
   *
   * ⛔ THE ROUTE DISCLOSES THIS AND THE CONSUMER DID NOT READ IT (GK-gg `bb2efc60`). Optional
   * because routes predate it; `undefined` must never be treated as `false` by inference —
   * read the flag, do not infer refusal from a null value.
   */
  ambiguous?: boolean;
  instrument_count?: number;
}

/**
 * `EMPTY` IS A STATE, NOT AN ERROR (spec §1.6): zero derived bars says NOTHING about tick
 * arrival, so it arrives as a 200 with a named reason — never a flat line at zero. The
 * open arm keeps an unheard state renderable instead of a compile error (SessionVerdict
 * precedent).
 */
export type CvdState = "PRESENT" | "EMPTY" | (string & {});

export interface CvdResponse {
  ok: true;
  root: string;
  cvd_state: CvdState;
  bars: CvdBar[];
  count: number;
  /** The EMPTY register's own words. Rendered verbatim; the panel never composes its own. */
  reason?: string;
  /** Distinct contracts in the window. >1 means the window spans a roll. */
  instruments?: string[];
  writers?: string[];
  study_status?: string;
  session_reset_rule?: string;
  /** Scoped to the SERVED WINDOW, not root-wide — series advancement, per GK-v's P1. */
  age_s: number | null;
  budget_s: number | null;
  stale: boolean | null;
  limit_capped?: boolean;
  /**
   * The rung the route ACTUALLY served, echoed back. OPTIONAL on purpose: a worker that does
   * not report it has told us NOTHING about the rung — it has not told us the rung is `1`.
   * ⇒ absent means NO MISMATCH CLAIM, the same law `bucketState` obeys for `src_bars`
   * (***UNREPORTED IS NOT ZERO***). Never default it to the requested tf: that would make the
   * lane certify agreement it never measured.
   */
  tf_applied?: string;
  tf_capped?: boolean;
}

/**
 * ⛔ `tf` WAS ABSENT FROM THIS SIGNATURE UNTIL R55a, AND THAT IS WHY THE LANE WAS PINNED.
 * The route has served `tf` since xx's CVD-per-TF contract (driven at 23:0xZ: tf=1/12/180 all
 * echo `tf_applied`), but this was the ONLY path to `/api/es/cvd` in the whole client and it
 * had no parameter to carry one. ⇒ the pane did not FORGET to pass a rung; it COULD NOT.
 * ***A HALF-DEPLOYED CONTRACT FAILS ON THE SIDE THAT CANNOT EXPRESS THE QUESTION.***
 * Omitting `tf` preserves the old call exactly — the route's own default rung.
 */
/**
 * `/api/es/greekflow` — the FLOW rail's read leg (BUILDER-ak S2a, keyed GK-mm `bh=5d837143`).
 *
 * ⛔ IT GOES THROUGH `getJson` LIKE EVERY OTHER ROUTE, AND THAT IS THE WHOLE POINT OF THIS
 * FUNCTION EXISTING. FlowRailPanel first hand-rolled `fetch(\`${globalThis.__API_BASE__ ?? ""}\`…)`,
 * which resolved to the EMPTY STRING in the browser and therefore to the PAGES origin. pages.dev
 * answers `/api/*` with **HTTP 200 and the SPA SHELL** — so the pane got a 200, no JSON, no root
 * echo, and rendered `root UNDECLARED` on the owner's page while the worker served 450 bars.
 * ⇒ ***A 200 FROM THE WRONG ORIGIN IS THE MOST EXPENSIVE KIND OF SUCCESS.*** `API_BASE` is the
 * single place that knows the worker host; anything that reaches around it is reaching for the
 * shell (this file's own §"workers.dev host, never pages.dev").
 *
 * `root` is REQUIRED and single-valued: greek-flow is POOLED (OPTIONSCHAT-j `bh=65654c6b`) and the
 * route 400s a two-root ask rather than union them. The caller names ONE root and reads back what
 * was SERVED from the envelope.
 */
export function fetchGreekFlow(root: string, limit = 240): Promise<GreekFlowResponse> {
  return getJson<GreekFlowResponse>(
    `/api/es/greekflow?root=${encodeURIComponent(root)}&limit=${limit}`,
  );
}

export interface DarkpoolLevelCluster {
  source_endpoint: string;
  trading_date: string;
  price_level: string;
  dark_pool_volume: string | null;
  regular_volume: string | null;
  lit_vol: string | null;
  off_vol: string | null;
  trading_date_source: string;
}

export interface DarkpoolRailReceipt {
  present: boolean;
  dates?: Record<string, unknown>;
  rows_total?: number;
  limit_capped?: boolean;
  served_trading_date?: string;
  newest_receipt_ts?: string;
  age_s?: number;
  stale?: boolean;
  reason?: string;
}

export interface DarkpoolLevelsResponse {
  ok: boolean;
  root: string;
  ticker: string;
  count: number;
  clusters: DarkpoolLevelCluster[];
  rails: Record<string, DarkpoolRailReceipt>;
  age_s: number | null;
  budget_s: number;
  stale: boolean | null;
  limit_capped: boolean;
  age_basis?: string;
  cluster_basis?: string;
  venue_state?: VenueState;
  served_at?: string;
}

export function fetchDarkpoolLevels(): Promise<DarkpoolLevelsResponse> {
  return getJson<DarkpoolLevelsResponse>("/api/darkpool/levels?ticker=SPY");
}

export interface GreekFlowResponse {
  ok: boolean;
  /** The root the route ACTUALLY served. Absent ⇒ the pane says so; it must never assume SPX. */
  root?: string;
  count: number;
  age_s: number | null;
  budget_s: number;
  stale: boolean | null;
  /** ORDER 114: the Worker calendar verdict is authoritative for the shared freshness element. */
  venue_state?: VenueState;
  bars: {
    minute_ts: string;
    ambiguous: boolean;
    dir_delta_flow: number | null;
    dir_vega_flow: number | null;
    transactions: number | null;
    volume: number | null;
  }[];
}

export function fetchCvd(limit = 300, tf?: string): Promise<CvdResponse> {
  return getJson<CvdResponse>(cvdPath(limit, tf));
}

/** ORDER 26-S2 — one fixture-backed read for both primary price panes. */
export function fetchVwapLevels(): Promise<VwapLevelsResponse> {
  return getJson<VwapLevelsResponse>("/api/es/vwap-levels");
}

const vwapStore = createVwapStore(fetchVwapLevels);
export const startVwapPolling = vwapStore.startVwapPolling;
export const subscribeVwap = vwapStore.subscribe;
export const resetVwapPolling = vwapStore.resetVwapPolling;
export const vwapFeedStore = vwapStore;

type ServedVenueStateListener = (state: VenueState | null, known: boolean) => void;
const servedVenueStateListeners = new Set<ServedVenueStateListener>();
let latestServedVenueState: VenueState | null = null;
let servedVenueStateKnown = false;

/** The admitted candles envelope is the app's one authoritative venue-verdict source. */
export function setServedVenueState(state: VenueState | null): void {
  latestServedVenueState = state;
  servedVenueStateKnown = true;
  vwapStore.setServedVenueState(state);
  noteTideScaleVenueState(state);
  for (const listener of servedVenueStateListeners) listener(state, true);
}

/** Fenced sibling consumers share the candle verdict without fetching the candle door again. */
export function subscribeServedVenueState(listener: ServedVenueStateListener): () => void {
  servedVenueStateListeners.add(listener);
  listener(latestServedVenueState, servedVenueStateKnown);
  return () => servedVenueStateListeners.delete(listener);
}
export interface WallLevel {
  root: "SPX";
  wall_strike_spx: number;
  wall_es: number;
  wall_gamma: number;
}

export interface WallAlertEvent {
  alert_id: string;
  root: "SPX";
  cvd_root: "ES";
  ts: string;
  push_contracts: number;
  progress_ticks: number;
  effort: number;
  wall_strike_spx: number;
  wall_es: number;
  wall_gamma: number;
  dist_ticks: number;
  into_wall: true;
  level: 1 | 2;
  open_window: boolean;
  expiry_day: boolean;
  study_status: "HYPOTHESIS";
}

export interface WallAlertRefusal {
  alert_id: string;
  refused: "BASIS_NOT_KNOWN_YET";
  basis_minute: string;
}

export interface WallAlertsResponse {
  ok: boolean;
  root?: "SPX";
  cvd_root?: "ES";
  state?: string;
  reason?: string;
  levels: WallLevel[];
  events: WallAlertEvent[];
  age_s?: number | null;
  budget_s?: number | null;
  stale?: boolean | null;
  study_status?: string;
  meta?: {
    state: string;
    refusals: WallAlertRefusal[];
  };
  venue_state?: VenueState;
}

export function fetchWallAlerts(limit = 50): Promise<WallAlertsResponse> {
  return getJson<WallAlertsResponse>(`/api/es/wall-alerts?limit=${Math.min(50, Math.max(1, Math.floor(limit)))}`);
}
/**
 * Dealer-book response. Mirrors the candles envelope so every panel reads staleness the same way.
 *
 * `root_verified` is NOT decoration: `/api/stock/<root>/greek-exposure/strike` currently returns
 * byte-identical payloads for SPX and SPXW (design decision D-3), so the ladder must tell the user
 * its feed is not root-separated. Carrying that as DATA rather than a hardcoded UI string means the
 * warning disappears by itself the day a root-distinct source lands, instead of surviving as a lie
 * because someone forgot to delete it.
 */
export interface DealerResponse extends Omit<FeedMeta, "symbol" | "tf" | "vendor_ts"> {
  root: string;
  root_verified: boolean;
  /**
   * NULLABLE here, unlike the price spine — and the difference is the point.
   * `es_1m_spine_v1` carries a vendor timestamp on every row, so `FeedMeta.vendor_ts` is
   * rightly non-null.
   *
   * ⚠ CORRECTED 08-07 ~20:5xZ (desk `c5dd4833` from BUILDER-d's measurement; RE-MEASURED HERE
   * against the live table before amending, because this reversed my own seat state's §2 D-4 note).
   * This comment used to read "as of 08-07 the greek-exposure endpoints are 100% populated, while
   * daily-resolution endpoints stay NULL". **THAT WAS BACKWARDS, AND IT DESCRIBED A RETIRED
   * WRITER'S FABRICATION.** The two endpoint families are not opposites — they are THE SAME CASE:
   *
   *   · `/api/stock/<root>/greek-exposure/strike` payload keys, measured on every stored 200:
   *     `call_charm, call_delta, call_gex, call_vanna, date, put_charm, put_delta, put_gex,
   *      put_vanna, strike` — **there is NO time field.** Only `date` ("2026-08-07").
   *   · By writer (the only valid discriminator — ratified `d0d3a728`, never a receipt_ts cutoff):
   *       interim-mac                              140/201 populated (69.7%)  ← FABRICATED
   *       uw_capture_v1@railway (durable, current)   0/80  populated (0.0%)   ← CORRECT
   *   · The fabrication signature: on interim rows `vendor_ts − receipt_ts` has a median of
   *     **−109,914s (−30.5 HOURS)** — it was deriving a timestamp from that `date` field, i.e.
   *     exactly the "midnight coerced into false precision" ledger V-3 ruled against for
   *     `oi-change`. Same defect, same class, opposite entry in this comment.
   *
   * ⇒ **THE GEX LADDER IS `vendor_ts: null` ALWAYS, AND FORWARD IT IS 0% BY DESIGN.** The durable
   * writer is right to null it. The contract below does not change — its null-when-none paragraph
   * already governs this correctly, which is why this is a comment fix and not a contract fix.
   *
   * PANEL CONSEQUENCE, binding: the dealer rail's age chip is **POLL AGE, LABELLED AS SUCH** —
   * never a vendor-time chip, and never silently. Same discipline SPX-009 landed for the tide.
   * Render the age you can prove per row, never per table.
   *
   * CONTRACT for whoever builds `/api/dealer/gex`: set `age_s` from `vendor_ts` when it exists,
   * and send `vendor_ts: null` when the source genuinely has none. The panel renders its poll-age
   * caveat off this field, so an honest null is what keeps the caveat truthful.
   */
  vendor_ts: string | null;
  venue_state?: VenueState;

  /**
   * PER-INSTRUMENT VERDICT, live since the 23:1xZ Worker deploy — and the reason this panel stopped
   * lying. `/api/dealer/gex` now serves the SAME verdict vocabulary as `/api/tide`, so the rail
   * reads it the same way: loud on STALE, quiet on CLOSED, visible-but-not-amber on anything
   * unrecognised. Optional because a rolled-back Worker does not send it.
   *
   * ⚠ THE DEFECT THIS FIELD CLOSES, recorded because it was LIVE and it was mine: the rail passed
   * `age_s`/`budget_s` to StalenessChip with NO `lastBarMs`, so it could never resolve CLOSED and
   * rendered AMBER on a market the server explicitly said was shut. Measured on production at
   * 23:1xZ — payload `session_verdict: "CLOSED"`, screen `"STALE 6m 33s"` in amber. That is the
   * exact defect DEP-2 was minted to prevent, arriving through the seam DEP-2 describes.
   */
  series?: SeriesReport[];

  /**
   * THE TWO SOURCES BEHIND ONE PANEL, each with its OWN budget and its own honest staleness — and
   * they are deliberately not collapsed into one number (B-3): the ladder is 390s DERIVED, the spot
   * 225s DECLARED because its band closed under measured copy lag. One value spanning two things
   * with different properties is the defect class this fleet hit six times on 08-07.
   *
   * ⚠ `binding_source` CORRECTED 2026-08-08 (HUDCHAT-b) — THIS COMMENT WAS WRONG AND THE LIVE
   * PAYLOAD IS WHAT EXPOSED IT. It used to read *"`binding_source` names which one the top-level
   * verdict is derived FROM"*, and then argued in its next breath that the ladder can never carry a
   * data-derived verdict. The live payload serves `binding_source: "ladder"` — which under that
   * reading claims exactly the impossible thing the same sentence forbids. Read at the source
   * (`workers/read-api/src/dealer.ts`), the field names **WHICH SOURCE MADE THE RESPONSE STALE**,
   * defaulting to the ladder when nothing is:
   *
   *     if (sources.ladder.stale) staleSources.push("ladder");
   *     if (sources.spot.stale)   staleSources.push("spot");
   *     const bindingSource = staleSources[0] ?? "ladder";
   *
   * ⇒ **The Worker is right; this comment was wrong.** Do NOT read `binding_source` as provenance
   * for the verdict — it is an ATTRIBUTION for the `stale` flag, and on a healthy response it is
   * `"ladder"` by default rather than by measurement.
   *
   * The clause that survives, because it is true and was verified at the source: **the INSTRUMENT
   * VERDICT comes from SPOT and the ladder consumes it.** `dealerSeriesClocks()` derives the clock
   * from the spot series alone; the ladder payload carries no time field, so a verdict of its own
   * would be fabricated provenance.
   */
  sources?: {
    ladder: { vendor_ts: string | null; receipt_ts: string | null; age_s: number | null; budget_s: number; stale: boolean };
    spot: { vendor_ts: string | null; receipt_ts: string | null; age_s: number | null; budget_s: number; stale: boolean };
  };
  binding_source?: string;
  spot: number | null;
  /** Optional underlying carried by the dealer snapshot itself. */
  underlying_price?: number | null;
  /**
   * ⚠ ADDED 2026-08-08 — THE PAYLOAD HAS CARRIED THIS ALL ALONG AND THE CONTRACT DID NOT DECLARE IT,
   * which is how a consumer came to render a spot failure as a market fact.
   *
   * `spot` and `strikes` fail INDEPENDENTLY: the ladder can arrive intact while spot extraction
   * fails, and then `spot: null` is a claim about OUR FEED while the strikes may still contain a
   * genuine gamma crossing. A consumer that collapses "no spot" into "no flip" states that the
   * cumulative curve never changed sign — **a fabricated claim about the market, made out of a
   * missing measurement.** `codex review` found exactly that in the REGIME chip (P1, 2026-08-08).
   *
   * Render this REASON VERBATIM wherever the distance cannot be computed. Null means spot is
   * present; a string means it is absent and the server is telling you why.
   */
  spot_absent_reason?: string | null;
  strikes: import("./dealer").GexStrikeRow[];
  count: number;
}

export function fetchDealer(root = "SPX"): Promise<DealerResponse> {
  return getJson<DealerResponse>(`/api/dealer/gex?root=${encodeURIComponent(root)}`);
}

/** One scope enters and exactly one route is requested; there is no fallback across quantities. */
export function fetchDealerScope(
  scope: DealerScope,
): Promise<DealerResponse | DealerLevelsResponse> {
  return getJson<DealerResponse | DealerLevelsResponse>(dealerScopePath(scope));
}

/**
 * `/api/dealer/levels` — TODAY'S 0DTE walls and flip (R407/R410/R411), ao's route `@e89d4fb`.
 *
 * ⛔ THIS IS NOT `/api/dealer/gex`, AND CONFLATING THEM IS THE DEFECT THAT NEARLY SHIPPED.
 * `gex` reads `/greek-exposure/strike` — ALL-EXPIRY, pooled, expiry-BLIND (proven by four hands).
 * On 2026-08-21 the two disagree by 300pt on the call wall and by the whole existence of the flip:
 * the pooled ladder offers 8800, the ruled 0DTE function returns **null**.
 * ⇒ *A missing line prompts investigation; a WRONG line does not.* The pooled ladder keeps
 * §STRUCTURE's panel (labelled "position · daily capture"); the CHART draws these.
 *
 * ⚠ EVERY LEVEL IS NULLABLE AND null IS A RESULT, NEVER A GAP TO FILL. `gamma_flip: null` means the
 * cumulative net never crosses at this expiry — the panel renders "no flip in range" and MUST NOT
 * borrow a number from anywhere else.
 */
export interface DealerLevelsResponse {
  refused_rows?: Record<string, number>;
  ok: boolean;
  /** Present only when `ok` is false — the server's own words, rendered verbatim. */
  reason?: string;
  root: string;
  /** Echoed from the vendor row, never assumed. LAW 2: SPX is not SPXW. */
  ticker: string;
  expiry: string;
  spot: number;
  /** Optional snapshot-native underlying, when a vendor payload carries one separately. */
  underlying_price?: number | null;
  /** SPX prices. null = the ruled function found none IN RANGE — a market state, not a fault. */
  call_wall: number | null;
  put_wall: number | null;
  gamma_flip: number | null;
  /** Each answer names its OWN window, because the windows genuinely differ. */
  walls_basis: string;
  flip_basis: string;
  /** The net is DECLARED, never relabelled — this endpoint has no call_gex/put_gex. */
  net_formula: string;
  /** Full-chain vendor gamma-OI rows. These are not GEX and must retain that identity in the UI. */
  strikes: Array<{
    strike: number;
    call_gamma_oi: number;
    put_gamma_oi: number;
    vendor_time: string;
  }>;
  strikes_near: number;
  strikes_total: number;
  /**
   * ⛔ THE OLDEST constituent row, not the newest. One live snapshot has carried NINE vendor times
   * spanning 7h17m (jj `78bf559d`), and `vendor_ts` is the NEWEST — a chip built on it reads 61s
   * while 48 rows are seven hours old.
   * ✅ SCOPED TO THE WALLS' OWN WINDOW as of ao's `@57625b1` — I raised it at `c387a680` when
   * `age_s` was the FULL chain while the walls came from the near-spot slice, which would have
   * chipped 7h STALE on levels 33s fresh. Verified against the LIVE route 18:1xZ:
   * `age_s 95` beside `chain_age_s 27443`. ⇒ **`age_s` is the age of the rows the WALLS were
   * computed from. That is the number the chip must carry.**
   */
  vendor_time_oldest: string | null;
  vendor_time_spread: string[];
  age_s: number | null;
  budget_s: number;
  stale: boolean | null;
  /**
   * The FULL chain's oldest, reported separately so the spread stays visible without
   * contaminating the walls' chip. 27,443s live while the walls were 95s — the two are different
   * facts about different windows and must never collapse into one chip.
   */
  chain_age_s?: number | null;
  chain_vendor_time_oldest?: string | null;
  chain_vendor_time_count?: number | null;
  /** The route states which rows `age_s` describes, rather than leaving the reader to infer it. */
  age_basis?: string;
  receipt_ts?: string | null;
  poll_seq?: number | null;
  /** No board-total aggregate while a snapshot may be bounded — per-strike levels only. */
  levels_only: boolean;
  venue_state?: VenueState;
}

export function fetchDealerLevels(origin = API_BASE): Promise<DealerLevelsResponse> {
  // ⛔ NO RANGE PARAMS. The route refuses `from`/`to` with a 400 rather than ignoring them, and
  // asking for one would be the moneyflow defect (accepts a window, honours neither) in a new place.
  return getJson<DealerLevelsResponse>("/api/dealer/levels", origin);
}

/**
 * `/api/macro/walls` — the top strip's MACRO chip (R485(2b)).
 *
 * ⛔ THE CHIP THIS FEEDS SPENT MONTHS RENDERING `NO FEED` OVER A ROUTE THAT DID NOT EXIST, AND
 * THEN OVER ONE THAT DID. CC-nn measured the served bundle (`index-BY-NDKOy.js`) and found
 * `macro/walls` **zero times** — there was no fetch to fail, so no Pages deploy could ever have
 * cured it. This function is the missing half.
 *
 * ⚠ IT GOES THROUGH `getJson`, NOT A BARE `fetch`. A bare relative fetch resolves against the
 * PAGES origin, and pages.dev answers `/api/*` with HTTP 200 and the SPA shell — a JSON parse of
 * an HTML document, surfacing as a nonsense error far from its cause. That is the exact trap
 * `Gex2Panel` shipped with at `4398c32`; it is documented one function down and is not being
 * repeated here.
 *
 * The response shape lives in `./macroWalls`, with the state decision, because `api.ts` cannot be
 * imported by Node's strip-only test runner — so nothing armable may live in this file.
 */
export function fetchMacroWalls(
  root = "ES",
): Promise<import("./macroWalls").MacroWallsPayload> {
  return getJson<import("./macroWalls").MacroWallsPayload>(
    `/api/macro/walls?root=${encodeURIComponent(root)}`,
  );
}

/**
 * `/api/dealer/ladder` — the §GEX-2 pane's data half (R424(1), BUILDER-ar `@c54af27`).
 *
 * ⛔⛔ THIS FUNCTION EXISTS BECAUSE THE PANE SHIPPED WITHOUT IT AND WOULD HAVE READ THE WRONG HOST.
 * `Gex2Panel` was parked at `4398c32` calling a BARE `fetch("/api/dealer/ladder")`. In the browser
 * that resolves against the PAGES origin, and pages.dev answers `/api/*` with **HTTP 200 and the SPA
 * shell** — so the pane would have taken a 200, failed to parse HTML as JSON, and rendered its
 * honest-empty fault box **for a reason that names the network instead of the mistake**.
 * ⇒ This is §"A 200 FROM THE WRONG ORIGIN IS THE MOST EXPENSIVE KIND OF SUCCESS" (above) landing a
 *   THIRD time — FlowRailPanel, then greekflow, now here. ***A LAW WRITTEN IN A COMMENT DOES NOT
 *   BIND A PANE THAT NEVER IMPORTS THE MODULE THE COMMENT LIVES IN.***
 * ⚠ The failure is worse than a blank: the fault box would have said "Unexpected token '<'", which
 *   reads as a broken worker. The route was fine. Only the host was wrong.
 *
 * `ts` selects the snapshot at-or-before an instant, for the scrubber. Omitted means NEWEST.
 * ⛔ Below `LADDER_FLOOR_TS` the route answers **400, not an empty ladder** — pre-06:34:21Z every
 * snapshot is a 2031-12-19 LEAP, and drawing those under a 0DTE heading is the scar R404 closed.
 */
export function fetchDealerLadder(ts: string | null = null): Promise<DealerLadderResponse> {
  const q = ts === null ? "" : `?ts=${encodeURIComponent(ts)}`;
  return getJson<DealerLadderResponse>(`/api/dealer/ladder${q}`);
}

/** Typed daily board, including the worker's signed net values and capture receipts. */
export interface StrikeExpiryTypedColumn {
  expiry: string; dte: number | null; board_date: string | null;
  state: "OK" | "INCOMPLETE" | "CAPTURE_REFUSED";
  checked_ts: string | null; stored_ts: string | null; poll_seq: string | null;
  receipt_rows: number | null; stored_rows: number; detail: string | null;
}
export interface StrikeExpiryTypedRow {
  expiry: string; strike: number;
  call_gex: number; put_gex: number; call_delta: number; put_delta: number;
  call_charm: number; put_charm: number; call_vanna: number; put_vanna: number;
  net_gex: number; net_delta: number; net_charm: number; net_vanna: number;
}
export type StrikeExpiryTypedResponse = {
  ok: true; door: "strike-expiry"; source: "typed"; root: "SPX";
  root_scope: "VENDOR_SPX_TICKER_POOLS_SPXW"; hypothesis: true;
  board_date: string | null; board_session: "CURRENT" | "PRIOR";
  age_s: number | null; budget_s: number; stale: boolean; venue: unknown;
  /** The door's own venue clock (ORDER_HUD_GEX_GRID_CLOSED_1 R1); absent on an older worker, never read as CLOSED. */
  venue_state?: { venue?: string | null; verdict?: string | null; last_session_open?: string | null; next_open?: string | null } | null;
  columns: StrikeExpiryTypedColumn[]; rows: StrikeExpiryTypedRow[]; census: unknown;
} | { ok: false; refused?: string; error: string; detail: string | null };

/** Preserve refusal bodies and HTTP status so the grid can name the door's refusal. */
export async function fetchStrikeExpiryTyped(): Promise<{ body: StrikeExpiryTypedResponse; status: number }> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/api/uw/strike-expiry?root=SPX&source=typed`, {
      signal: ctl.signal, headers: { Accept: "application/json" },
    });
    let body: StrikeExpiryTypedResponse;
    try { body = await res.json(); }
    catch { throw new FeedError("PAYLOAD_INVALID", res.status, "typed grid returned non-JSON"); }
    return { body, status: res.status };
  } catch (err) {
    if (err instanceof FeedError) throw err;
    throw new FeedError(ctl.signal.aborted ? "QUERY_TIMEOUT" : "NETWORK_ERROR", 0,
      err instanceof Error ? err.message : String(err));
  } finally { clearTimeout(timer); }
}

/** ORDER_HUD_UW_FACE_1_BOX2 Part C: the same typed door with window=7d (spx 872edf61) serves sum_7d beside the grid. */
export type StrikeExpirySum7dResponse = (Extract<StrikeExpiryTypedResponse, { ok: true }> & {
  sum_7d: {
    window: "7d"; basis: string; expiries: string[]; excluded: { expiry: string; state: string }[]; gaps: string[];
    beyond_window: string[]; board_dates: string[]; complete: boolean;
    rows: { strike: number; expiries_n: number; net_gex: number; net_delta: number; net_charm: number; net_vanna: number }[];
  };
}) | Extract<StrikeExpiryTypedResponse, { ok: false }>;

/** A refused sum never blanks the grid: its own fetch, its own refusal, the same handling as the grid's. */
export async function fetchStrikeExpirySum7d(): Promise<{ body: StrikeExpirySum7dResponse; status: number }> {
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${API_BASE}/api/uw/strike-expiry?root=SPX&source=typed&window=7d`, {
      signal: ctl.signal, headers: { Accept: "application/json" },
    });
    let body: StrikeExpirySum7dResponse;
    try { body = await res.json(); }
    catch { throw new FeedError("PAYLOAD_INVALID", res.status, "7-day sum returned non-JSON"); }
    return { body, status: res.status };
  } catch (err) {
    if (err instanceof FeedError) throw err;
    throw new FeedError(ctl.signal.aborted ? "QUERY_TIMEOUT" : "NETWORK_ERROR", 0,
      err instanceof Error ? err.message : String(err));
  } finally { clearTimeout(timer); }
}

/** One near-spot strike row, exactly as `gexLadder.ts::LadderStrike` lands it. */
export interface DealerLadderStrike {
  strike: number;
  call_gamma_oi: number;
  put_gamma_oi: number;
  net: number;
  /** THIS ROW's vendor time. Per-row, never the snapshot's — one snapshot has spanned 7h17m. */
  time: string;
  dist: number;
  /**
   * ⛔ NULL IS NOT ZERO. Null = this strike was not in BOTH snapshots, so no change was MEASURED.
   * Zero = a change was measured and it was zero. The glyph layer must never collapse them.
   */
  delta: number | null;
  delta_basis: "paired" | "coverage_added" | "no_previous_snapshot";
  /** Server-computed against the SNAPSHOT's own newest vendor time — not against wall clock. */
  stale: boolean;
}

export interface DealerLadderResponse {
  refused_rows?: Record<string, number>;
  ok: boolean;
  /** Present only when `ok` is false — rendered verbatim, never paraphrased. */
  reason?: string;
  root: string;
  /** Echoed from the vendor row. LAW 2: SPX is not SPXW. */
  ticker: string;
  expiry: string;
  spot: number;
  /** ⚠ R422 OPEN. The pane DISCLOSES this rather than trusting it silently. */
  spot_provenance: string;
  strikes: DealerLadderStrike[];
  near_count: number;
  chain_count: number;
  paired: number;
  /**
   * ⛔ COVERAGE IS NOT FLOW. At 18:05:15Z pagination took the chain 500 -> 590 rows; a naive diff
   * paints 90 PHANTOM ADDS in the exact glyph the owner reads as dealer intent. These two counts
   * are how the pane SAYS a coverage change happened instead of drawing it as accumulation.
   */
  coverage_added: number;
  coverage_dropped: number;
  delta_basis: string;
  net_formula: string;
  newest_vendor_time: string | null;
  /** False ⇒ the previous snapshot was a different root or expiry; the reason is served with it. */
  prev_comparable: boolean;
  prev_incomparable_reason: string | null;
  scrub_floor_ts: string;
  scrub_floor_basis: string;
  ts_applied: string | null;
  receipt_ts?: string | null;
  poll_seq?: number | string | null;
  levels_only: boolean;
  venue_state?: VenueState;
  /**
   * ⛔ THE STALENESS CHIP, SERVER-SIDE — ar's `@d559fc2`, discharging GK-rr's third block item.
   *
   * It was ABSENT at `@c54af27`, and the pane's first cure here was a named-absence chip, because
   * the alternative on offer was differencing a SERVER stamp against the BROWSER clock — the
   * confound CC-jj measured today, where a laptop 40s adrift chips STALE on a live feed.
   * ⇒ ar cured it at the RIGHT END instead: *"age_s must be SERVER-SIDE, never client-inferred."*
   *
   * ⛔⛔ AND IT IS THE **OLDEST** VENDOR TIME, NOT THE NEWEST — the two answer different questions
   * and collapsing them is how a 33-second-fresh wall got painted 7 hours stale (ao `@57625b1`,
   * keyed twice). `newest_vendor_time` drives the PER-ROW grey (*is this row behind its siblings?*);
   * `oldest_vendor_time` drives the PANEL chip (*is this ladder behind the market?*).
   * ⇒ ***TWO QUESTIONS, TWO SELECTIONS, ONE SCOPED SET.***
   */
  age_s?: number | null;
  oldest_vendor_time?: string | null;
  /**
   * ⚠ THE BUDGET IS SERVED, AND THE PANE MUST NOT KEEP ITS OWN. This is dealer.ts's declared
   * `LADDER_BUDGET_S` (390, ruled — desk `a8a03168`, GK-b `e38fe3d2`), already pinned inside a
   * strict dead band by dealer.selftest. A local default in the component would be a SECOND budget
   * for one instrument, which is the fork this fleet keeps paying for. ONE BUDGET, NOT TWO.
   */
  budget_s?: number;
  /** The route's own verdict. Read it; do not recompute a second opinion beside it. */
  stale?: boolean;
  age_basis?: string;
}

/**
 * `/api/market/market-tide` + the SPY 1m spot series the overlay needs (desk ruling cb5c649f).
 *
 * `spot` IS NULLABLE AND THAT IS THE WHOLE CONTRACT. The desk's second binding condition is a LOUD
 * degrade to tide-only when spot is absent — never draw one series silently. A missing overlay must
 * therefore arrive as `null`, not as `[]`: an empty array reads as "SPY printed nothing", which is a
 * claim about the market, while null is a claim about our feed. The panel says different things.
 *
 * SERVER CONTRACT, owed by whoever builds this route (BUILDER-a, packaged with `/api/dealer/gex` as
 * one read-API lane):
 *
 * 1. Select the newest row per endpoint with `http_status = 200` (ledger D-6 — failed attempts are
 *    stored as rows, and a naive newest-row read returned a 503 with a NULL payload on THIS endpoint
 *    at 10:04:57Z on 08-07).
 *
 * 2. **DO NOT SURFACE `vendor_ts` AS KNOWLEDGE TIME FOR THIS SERIES — its semantics are currently
 *    EPOCH-HETEROGENEOUS** (desk 271cae92, from the SPX-001 cutover seam):
 *      · interim-writer rows  → `vendor_ts` = the OLDEST element (hours stale inside its own payload)
 *      · durable-worker rows  → `vendor_ts` = the newest NON-FUTURE element (true knowledge time)
 *    Both epochs are in the table at once until the interim retires, so a single interpretation is
 *    wrong for half the rows whichever one you pick. Flip to `vendor_ts` only after cutover
 *    completes, and flip WITH the epoch. Until then the panel derives knowledge time from the series
 *    it actually draws, which is correct under both epochs and needs no migration.
 */
/**
 * R-3's verdict, served per instrument. TWO members are easy to get wrong:
 *
 * · `null` is a REAL case, not a gap in the union. BUILDER-a's degrade path returns a null verdict
 *   plus a reason naming the missing symbol when the calendar has no rows. Render "no verdict",
 *   never LIVE.
 * · `(string & {})` keeps the union OPEN on purpose. The server is growing NAMED states — the first
 *   is `SESSION_END_PUBLISH_LAG` (BUILDER-a, @b926fcb) — and a closed union would make every new
 *   one a compile error in a client that should simply render it. The literals stay for the
 *   autocomplete and the exhaustive checks that matter; the open arm stops the contract from
 *   breaking the moment the server says something true that this client has not heard before.
 */
export type SessionVerdict = "LIVE" | "CLOSED" | "STALE" | (string & {}) | null;

/** Venue calendar decision served by the Worker; the browser never derives closures. */
export interface VenueState {
  last_session_open?: string | null;
  current_session?: { open: string; close: string } | null;
  venue: string | null;
  verdict: Exclude<SessionVerdict, null> | "UNKNOWN_VENUE";
  closure: {
    date: string;
    kind: "FULL" | "EARLY";
    name: string;
    close_local: string | null;
    verified: boolean;
  } | null;
  last_close: string | null;
  next_open: string | null;
}

/**
 * VERDICTS THIS CLIENT UNDERSTANDS AND HAS DELIBERATELY RULED QUIET.
 *
 * Membership is a DESIGN DECISION with a reason, never a convenience:
 * · LIVE                     — fresh, nothing to say.
 * · CLOSED                   — a fact about the world. Amber here is the false alarm this whole
 *                              panel was held back a day to avoid.
 * · SESSION_END_PUBLISH_LAG  — HUDCHAT's call, routed explicitly by BUILDER-a (@b926fcb) and ruled
 *                              INFORMATIONAL here. It fires for the last ~7 minutes of EVERY session
 *                              on a HEALTHY feed (the vendor batches the close-tail), it is
 *                              STRUCTURALLY bounded at window+period = 900s rather than fitted to a
 *                              sample, and BEYOND that bound it falls through to STALE. That last
 *                              clause is what makes quiet safe: the loud path still exists, so this
 *                              is a named explanation and not a hiding place. An amber that fires on
 *                              schedule at every close is precisely how a cockpit trains its reader
 *                              to ignore amber.
 */
export const QUIET_VERDICTS: readonly string[] = ["LIVE", "CLOSED", "SESSION_END_PUBLISH_LAG"];

/**
 * Per-instrument freshness + session verdict (SPX-010, live since the 21:34Z deploy).
 *
 * ⚠ THIS ARRAY IS WHY THE PANEL MUST NOT READ THE TOP-LEVEL `budget_s`. Measured on the live edge
 * at 21:37Z, the two disagree ON PURPOSE: top-level `poll_age_s: 242` against `budget_s: 180` (plan
 * Q2's flat number) reads OVER BUDGET, while MARKET's own entry reads `CLOSED` with a derived 480s
 * budget — a correctly shut market. Reading the flat pair would put the panel amber on a healthy
 * closed feed, which is the exact defect that kept this panel unmounted for a day.
 *
 * TWO INSTRUMENTS, TWO SESSIONS, AND THEY DIVERGE FOR ~3h50m EVERY WEEKDAY (measured):
 *     MARKET  09:30 → 16:10 ET      SPY  09:30 → 20:00 ET
 * so between 16:10 and 20:00 one is legitimately CLOSED while the other is legitimately LIVE. There
 * is deliberately NO aggregate verdict here: a single one would be wrong for one of them all
 * afternoon. Render them independently or not at all.
 */
/**
 * THE VERDICT FIELDS EVERY PER-INSTRUMENT REPORT CARRIES, wherever it is served.
 *
 * Extracted rather than duplicated because two surfaces now serve the same verdict vocabulary —
 * `/api/tide`'s `series[]` (MARKET, SPY) and `/api/health`'s `symbols[]` (ES1!, VIX) — and a panel
 * reading either must apply the SAME rules: loud on STALE, quiet on the QUIET_VERDICTS allowlist,
 * visible-but-not-amber on anything it does not recognise. Duplicating the shape would let those
 * rules drift apart per panel, which is how one surface ends up reddening on a state the other
 * treats as normal.
 *
 * ⚠ THE TWO ENVELOPES ARE NOT THE SAME TYPE AND MUST NOT BE MERGED. `/api/health` carries
 * `last_bar` + `vendor_ts` and NO `knowledge_ts`; `/api/tide` carries `knowledge_ts` +
 * `detects_single_missing_bar` and neither of the others. Modelling one as an extension of the
 * other would claim fields the payload does not have — measured on both live payloads, not assumed.
 */
export interface InstrumentVerdict {
  symbol: string;
  root: string;
  budget_s: number;
  budget_basis: string;
  age_s: number | null;
  stale: boolean;
  session_verdict: SessionVerdict;
  session_reason: string | null;
  venue_state?: VenueState;
  session_basis: {
    tz: string;
    segment: {
      symbol: string;
      root: string;
      tz: string;
      open_dow: number;
      open_time: string;
      close_dow: number;
      close_time: string;
    } | null;
  } | null;
}

export interface SeriesReport {
  symbol: string;
  root: string;
  budget_s: number;
  /** `derived-from-band` when a dead band survives measurement; `declared-not-derivable` when it does not. */
  budget_basis: string;
  /**
   * Whether this budget can detect ONE missing bar. SPY ships `false` — we poll a 60s series every
   * 300s, so its band is empty by 240s. A budget that cannot detect the thing it is named for has to
   * say so rather than imply coverage it does not have; the panel renders that admission, not a number.
   */
  detects_single_missing_bar: boolean;
  /** Knowledge time of the newest bar for THIS series. Null when nothing has been received. */
  knowledge_ts: string | null;
  age_s: number | null;
  stale: boolean;
  /** CLOSED and `stale: true` arrive TOGETHER and that pair is correct — see the note above. */
  session_verdict: SessionVerdict;
  /** Server's own words for the verdict. Rendered verbatim; the panel never composes its own. */
  session_reason: string | null;
  venue_state?: VenueState;
  /** The evidence behind the verdict — timezone and the calendar segment it was evaluated against. */
  session_basis: {
    tz: string;
    segment: {
      symbol: string;
      root: string;
      tz: string;
      open_dow: number;
      open_time: string;
      close_dow: number;
      close_time: string;
    } | null;
  } | null;
}

export interface TideResponse {
  root: string;
  tide: import("./tide").TideRow[];
  spot: import("./tide").SpotBar[] | null;
  /** Why spot is absent, when it is. Rendered verbatim — the panel does not invent a reason. */
  spot_absent_reason: string | null;
  /**
   * PER-SERIES verdicts — the authority for staleness on this panel. Optional in the TYPE because a
   * Worker older than the 21:34Z deploy does not send it, and a client that assumed its presence
   * would throw against a rollback rather than degrade. Absent ⇒ fall back to stating the coverage
   * window as fact, exactly as this panel did before the verdict existed.
   */
  series?: SeriesReport[];
  venue_state?: Record<string, VenueState>;
  /**
   * ⚠ POLL age and plan Q2's FLAT budget. NOT the data's age and NOT the per-instrument budget.
   * Kept because the poll age is still worth showing as a claim about OUR process — but staleness
   * verdicts come from `series`, never from this pair. See the SeriesReport note.
   */
  poll_age_s: number;
  budget_s: number;
  served_at: string;
}

export function fetchTide(): Promise<TideResponse> {
  return getJson<TideResponse>(`/api/tide`);
}

/** One ORDER 3b cell only. Scope is encoded in the path and re-validated by the consumer. */
export function fetchExpiryTide(
  tide: ExpiryTide,
  expiration: ExpirationScope,
): Promise<unknown> {
  return getJson<unknown>(expiryTidePath(tide, expiration));
}

/**
 * `/api/es/news` — SPX-044's point-in-time news read (workers.dev host, never pages.dev).
 *
 * THE RESPONSE'S THREE HONEST ABSENCES ARE THE CONTRACT (see lib/newsFeed.ts):
 *   · a 404 arrives HERE as a thrown FeedError with `status: 404` — "no snapshot strictly
 *     before T" is a fact about the capture lane and the panel renders it as its own state,
 *     never as a generic error and never as an empty list;
 *   · a 200 with `count: 0` resolves normally — a quiet window under a sparse filter
 *     (yield is day-class dependent; see measured_yield_applicable);
 *   · a 200 with `stale: true` resolves normally too — data, over budget, chip goes loud.
 *
 * ⛔ `relevance` and `limit` are the ONLY knobs this client turns. The budget is NOT a
 * parameter by the route's design — a caller who could widen its own budget could render a
 * stale rail green — so no budget ever rides this query string.
 */
export function fetchNews(
  relevance = "index_or_macro",
  limit = 50,
): Promise<import("./newsFeed").NewsResponse> {
  return getJson<import("./newsFeed").NewsResponse>(
    `/api/es/news?relevance=${encodeURIComponent(relevance)}&limit=${limit}`,
  );
}

/** Human-readable age for a staleness chip: "8s", "3m 12s", "1h 04m". */
export function formatAge(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m < 60) return `${m}m ${String(s).padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${String(m % 60).padStart(2, "0")}m`;
}

/**
 * `/api/health` — the SPINE's per-instrument reading (ES1!, VIX), which is where the top strip's two
 * data instruments get their verdicts (SPX-018).
 *
 * ⚠ THIS IS A DIFFERENT ENDPOINT FROM THE TIDE'S VERDICTS AND THE DISTINCTION HAS ALREADY COST TIME.
 * `/api/health` serves **ES1! and VIX only**. MARKET and SPY are per-SERIES on `/api/tide`, by
 * BUILDER-a's design, because that is where the panel reading them actually reads. This seat spent
 * several polls watching `/api/health` for MARKET/SPY, which were never going to appear there.
 *
 * ⚠ AND `ok` IS NARROWER THAN IT LOOKS. The aggregate keys on ES1!+VIX; MARKET/SPY are NOT in it
 * (BUILDER-d found it, BUILDER-a owns it, fix @26c7af5 deployed nowhere), so a total MARKET or SPY
 * outage cannot turn it red. Read the per-symbol entries, never `ok` alone, for any instrument.
 */
export interface HealthSymbol extends InstrumentVerdict {
  tf: string;
  bars: number;
  /** Newest bar's own timestamp. This, not `receipt_ts`, is what CLOSED is corroborated against. */
  last_bar: string | null;
  vendor_ts: string | null;
  /** The POLL instant. Never a row identity and never a data age — ledger rule, learned twice. */
  receipt_ts: string | null;
}

export interface HealthResponse {
  ok: boolean;
  reason: string | null;
  symbols: HealthSymbol[];
  heartbeats: { worker: string; last_ok: string; cycles: number; note: string }[];
  served_at: string;
  venue_state?: Record<string, VenueState>;
}

export function fetchHealth(): Promise<HealthResponse> {
  return getJson<HealthResponse>(`/api/health`);
}

/**
 * Is this verdict one the client has been taught to keep quiet about?
 *
 * Shared by every panel that renders a verdict, so the rules cannot drift per surface. Returns
 * `"quiet"` for a known-benign state, `"loud"` for STALE, and `"unknown"` for anything else —
 * which callers must render VISIBLY and NOT as amber, because a verdict this client cannot
 * interpret is neither health nor fault, and asserting either would be a lie.
 */
export function verdictTone(v: SessionVerdict): "quiet" | "loud" | "unknown" | "none" {
  if (v === null || v === undefined) return "none";
  if (v === "STALE") return "loud";
  return QUIET_VERDICTS.includes(v) ? "quiet" : "unknown";
}

/**
 * MONEY-FLOW — BUILDER-zz's route (`/api/es/moneyflow`, served 20:22Z, ver 02e23478).
 *
 * ⛔ THIS ENVELOPE WAS NARROWER THAN `FeedMeta` AND THE PANEL PAID FOR IT — UNTIL 21:14Z.
 * Measured at the served edge, scoped to the handler (bus `593d1e63`): the route returns
 * `root · tf_applied · bars · count` and NOTHING else — no `age_s`/`budget_s`/`stale`
 * (law 10 budgets flow at 3m) and no `expected_src_bars` (without which a partiality claim
 * is unmakeable). It is NOT typed as `FeedMeta`, because declaring fields the server does
 * not send is how a panel comes to render `undefined` as fresh.
 * ⇒ ***A TYPE THAT PROMISES MORE THAN THE WIRE CARRIES IS A LIE WITH A COMPILER BEHIND IT.***
 * The ask for the server-side fields is open at `593d1e63`; until it lands the panel derives
 * both, and every derived number renders WITH ITS BASIS.
 */
export interface MoneyFlowBarWire {
  /** ISO string on the wire — the panel parses to epoch ms. */
  t: string;
  mf: number | null;
  close: number;
  src_bars: number;
  volume_incomplete: boolean;
}

export interface MoneyFlowResponse {
  root: string;
  tf_applied: string;
  /**
   * BUILDER-zz's staleness envelope (served 21:14Z, ver 5b7e2615) — the fields this client
   * used to DERIVE and no longer does. `age_s` is measured server-side from `max(receipt_ts)`:
   * when WE learned the bar, not when the market printed it, so a dead writer cannot masquerade
   * as a quiet market.
   */
  age_s: number | null;
  budget_s: number;
  /** NULL when the age is unknown — never `false`, which is the green a chip paints. */
  stale: boolean | null;
  /** NULL when the window cannot establish a normal; the basis says why. */
  expected_src_bars: number | null;
  expected_src_bars_basis: string;
  bars: MoneyFlowBarWire[];
  count: number;
}

/** `tf` is REQUIRED — the route 400s without it, deliberately (zz `574b1f26` ③). */
export function fetchMoneyFlow(tf: string, limit = 240): Promise<MoneyFlowResponse> {
  return getJson<MoneyFlowResponse>(`/api/es/moneyflow?tf=${encodeURIComponent(tf)}&limit=${limit}`);
}

/**
 * SPX net-premium (R677①). ⛔ RETURNS `unknown` ON PURPOSE.
 *
 * Every other fetcher here names a response type, and that is right for a route whose shape has
 * been served and read. This one has NOT: `/api/spx/net-prem` landed at `2232b46` and was still
 * 404 at 23:49Z (landed-not-served). Declaring a type here would assert a shape nobody has
 * received — the `gex2` trap, where a pane guessed three field names and the one that hurt made
 * a MISSING chip look like a healthy pane.
 *
 * `readNetPrem` in `netPrem.ts` is total over `unknown` and refuses loudly, so the validation
 * lives there and the type does not lie here.
 */
export function fetchNetPrem(limit?: number): Promise<unknown> {
  return getJson<unknown>(`/api/spx/net-prem${limit === undefined ? "" : `?limit=${limit}`}`);
}
